import { withClientContext } from "~/apollo/client";
import { DdqProdOpsWorkflowStatus, DdqProdOpsWorkflowType } from "~/graphql/supergraphEnums";
import { useGetDdqQueueSummaryCountsQuery } from "~/graphql/supergraphHooks";
import { DEFAULT_STATUSES } from "../useDdqQueueFilters";
import type { ApolloError } from "@apollo/client";

export type DdqQueueSummaryCounts = {
  workflowType: Partial<Record<DdqProdOpsWorkflowType, number>>;
  status: Partial<Record<DdqProdOpsWorkflowStatus, number>>;
  dueToday: number;
  overdue: number;
};

export type UseDdqQueueSummaryCountsResult = {
  counts: DdqQueueSummaryCounts;
  // "Due today"/"overdue" are bucketed from a capped fetch (see queries.federation.gql), so
  // they can undercount -- this says whether to render "N+" instead of a bare number.
  isDueBucketTruncated: boolean;
  isLoading: boolean;
  // True once the query has settled with no usable data at all -- render "-", not a 0 that
  // reads as "there are none" when the real answer is "we don't know".
  isUnavailable: boolean;
  error?: ApolloError;
};

type DueBucketItem = { dueAt: string | null };

const isToday = (date: Date, now: Date): boolean =>
  date.getFullYear() === now.getFullYear() &&
  date.getMonth() === now.getMonth() &&
  date.getDate() === now.getDate();

// Splits the due-bucket fetch's rows into "due today" / "overdue" by calendar day, in the
// viewer's local timezone. Anything without a parseable dueAt (shouldn't happen for the
// statuses this query asks for) is silently excluded from both buckets.
export function bucketDueDates(
  items: DueBucketItem[],
  now: Date = new Date(),
): { dueToday: number; overdue: number } {
  let dueToday = 0;
  let overdue = 0;
  for (const item of items) {
    if (!item.dueAt) continue;
    const due = new Date(item.dueAt);
    if (Number.isNaN(due.getTime())) continue;
    if (isToday(due, now)) dueToday += 1;
    else if (due.getTime() < now.getTime()) overdue += 1;
  }
  return { dueToday, overdue };
}

export const useDdqQueueSummaryCounts = (): UseDdqQueueSummaryCountsResult => {
  const { data, loading, error } = useGetDdqQueueSummaryCountsQuery({
    context: withClientContext("federation"),
    variables: { defaultStatuses: DEFAULT_STATUSES },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const dueBucketItems = data?.dueBucket?.items ?? [];
  const dueBucketTotalCount = data?.dueBucket?.totalCount ?? 0;
  const { dueToday, overdue } = bucketDueDates(dueBucketItems);

  return {
    counts: {
      workflowType: {
        [DdqProdOpsWorkflowType.DdqRequest]: data?.ddqRequestCount?.totalCount ?? 0,
        [DdqProdOpsWorkflowType.OriginalFormat]: data?.originalFormatCount?.totalCount ?? 0,
        [DdqProdOpsWorkflowType.BackfillDdq]: data?.backfillDdqCount?.totalCount ?? 0,
      },
      status: {
        [DdqProdOpsWorkflowStatus.Pending]: data?.notStartedCount?.totalCount ?? 0,
        [DdqProdOpsWorkflowStatus.OnHold]: data?.onHoldCount?.totalCount ?? 0,
      },
      dueToday,
      overdue,
    },
    isDueBucketTruncated: dueBucketItems.length < dueBucketTotalCount,
    isLoading: loading,
    isUnavailable: Boolean(error) && !data,
    error,
  };
};
